import { Injectable } from '@angular/core'
import { APP_CONFIG } from '../config/environment'
import { AuthService } from './auth.service'

interface ColumnSchema {
  type?: string
  format?: string
}

type ColumnSchemaMap = Map<string, ColumnSchema>

@Injectable({
  providedIn: 'root',
})
export class PostgrestSchemaService {
  private readonly baseUrl = APP_CONFIG.postgrestBaseUrl
  private schemaPromise?: Promise<unknown>
  private readonly columnCache = new Map<string, ColumnSchemaMap>()
  private readonly ilikeCompatibleCache = new Map<string, Set<string>>()

  constructor(private readonly authService: AuthService) {}

  async getIlikeCompatibleColumns(table: string): Promise<Set<string>> {
    if (!this.ilikeCompatibleCache.has(table)) {
      const columns = await this.getTableColumnMap(table)
      const compatible = new Set<string>()

      columns.forEach((schema, column) => {
        if (this.isTextual(schema)) {
          compatible.add(column)
        }
      })
      compatible.add('merchant_name')
      compatible.add('cash_advance_name')
      compatible.add('terminal_name')

      this.ilikeCompatibleCache.set(table, compatible)
    }

    return this.ilikeCompatibleCache.get(table) ?? new Set<string>()
  }

  private isTextual(schema: ColumnSchema): boolean {
    const format = this.normalize(schema.format)
    if (format && this.isPlainTextFormat(format)) {
      return true
    }

    const type = this.normalize(schema.type)
    if (!format && type && this.isPlainTextType(type)) {
      return true
    }

    return false
  }

  private isPlainTextType(type: string): boolean {
    return type === 'string'
  }

  private isPlainTextFormat(format: string): boolean {
    return (
      format === 'text' ||
      format === 'citext' ||
      format === 'varchar' ||
      format.startsWith('character varying')
    )
  }

  private normalize(value: string | undefined): string | undefined {
    return value?.toLocaleLowerCase().trim()
  }

  private async getTableColumnMap(table: string): Promise<ColumnSchemaMap> {
    if (this.columnCache.has(table)) {
      return this.columnCache.get(table)!
    }

    const document = await this.fetchOpenApiDocument()
    const schema = this.extractTableSchema(document, table)
    const map: ColumnSchemaMap = new Map()

    const properties = schema?.properties ?? {}
    Object.entries(properties).forEach(([column, definition]) => {
      map.set(column, this.resolveColumnSchema(definition, document))
    })

    this.columnCache.set(table, map)
    return map
  }

  private extractTableSchema(document: any, table: string): any {
    return (
      document?.components?.schemas?.[table] ??
      document?.definitions?.[table] ??
      document?.components?.schemas?.[this.toPascalCase(table)]
    )
  }

  private toPascalCase(value: string): string {
    return value
      .split('_')
      .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
      .join('')
  }

  private resolveColumnSchema(definition: any, document: any): ColumnSchema {
    if (!definition) {
      return {}
    }

    if (definition.$ref && typeof definition.$ref === 'string') {
      const resolved = this.resolveRef(definition.$ref, document)
      return this.resolveColumnSchema(resolved, document)
    }

    return {
      type: definition.type,
      format: definition.format,
    }
  }

  private resolveRef(ref: string, document: any): any {
    const path = ref.replace(/^#\//, '').split('/')
    return path.reduce(
      (acc, segment) =>
        acc && acc[segment] !== undefined ? acc[segment] : undefined,
      document,
    )
  }

  private async fetchOpenApiDocument(): Promise<any> {
    if (!this.schemaPromise) {
      this.schemaPromise = this.downloadOpenApiDocument().catch((error) => {
        this.schemaPromise = undefined
        throw error
      })
    }

    return this.schemaPromise
  }

  private async downloadOpenApiDocument(): Promise<any> {
    const token = await this.authService.getPostgrestJwtToken()
    const response = await fetch(this.baseUrl, {
      method: 'GET',
      headers: {
        Authorization: `Bearer ${token}`,
        Accept: 'application/openapi+json, application/json',
      },
    })

    if (!response.ok) {
      throw new Error(
        `Failed to load PostgREST schema: ${response.status} ${response.statusText}`,
      )
    }

    return response.json()
  }
}
